import {
  LogicBlockConfig,
  LogicBlockIndex,
  BlockRule,
} from '@morph-mapper/node-logic';
import { OptionEntry } from '@morph-mapper/types';
import { getKeys, getValues } from '@morph-mapper/utils';

export type GraphId = string;
export type NodeId = string;
export type EdgeId = string;
export type PortId = string;
export type FieldId = string;
export type CommandId = string;

export enum GraphRefType {
  Field = 'field',
  Param = 'param',
}

/**
 * A graph can have 2 semantic definitions:
 * (1) The graph provides a definition for a field which is part of the template,
 *      and thus the computed result of the graph maps directly to the value of the field.
 * (2) The graph provides a definition for a configuration parameter of a node which itself is part of another graph,
 *      the computed output thus maps to the value of the configuration parameter.
 */
export type GraphRef =
  | { kind: GraphRefType.Field; fieldId: FieldId }
  | {
      kind: GraphRefType.Param;
      graphId: GraphId;
      fieldId: FieldId;
      ownerNodeId: NodeId;
      paramKey: string;
    };

/**
 * The port direction indicates if data flows in or out of a node.
 */
export enum PortDirection {
  Input = 'input',
  Output = 'output',
}

/**
 * Inferred types, which are created based on TypeExpr and reside in the computed cache.
 */
export type TypeRef =
  | { kind: 'unknown' }
  | { kind: 'bool' }
  | { kind: 'number' }
  | { kind: 'text' }
  | { kind: 'list'; of: TypeRef }
  | { kind: 'table' }
  | { kind: 'object'; schemaId?: string }
  | { kind: 'union'; of: TypeRef[] };

/**
 * Type definition for what a node consumes or produces, forming a typed contract on the node ports.
 */
export type TypeExpr =
  | { kind: 'concrete'; type: TypeRef }
  | { kind: 'var'; name: string }
  | { kind: 'list'; of: TypeExpr }
  | { kind: 'union'; of: TypeExpr[] };

export type InferredPortType = {
  declared: TypeExpr;
  inferred: TypeRef;
  confidence: 'certain' | 'tentative';
};

/**
 * Ports act as interfaces for a node, they determine input and output data flows.
 */
export interface PortDefinition {
  id: PortId;
  key: string;
  direction: PortDirection;
  // The expected type of the ingested data for input ports, the produced type of the data for output ports.
  type: TypeExpr;
  // Flag which indicates if a port must have another node attached.
  required: boolean;
  // Flag which indicates if a port can have multiple inputs/outputs.
  multi: boolean;
}

type NodeOptionsWrapper<T> = {
  [key in keyof T]: OptionEntry<unknown>;
};

// TODO: Can we resolve the any in the options type?
export interface NodeInstance<T extends LogicBlockIndex = LogicBlockIndex> {
  id: NodeId;
  kind: LogicBlockIndex;
  options: NodeOptionsWrapper<
    LogicBlockConfig<T, Set<BlockRule>, any>['options']
  >;
}

export interface EdgeInstance {
  id: EdgeId;
  order: number;
  from: {
    nodeId: NodeId;
    portId: PortId;
  };
  to: {
    nodeId: NodeId;
    portId: PortId;
  };
}

export interface Graph {
  id: GraphId;
  ref: GraphRef;

  graphVersion: number;
  topologyVersion: number;

  nodes: Record<NodeId, NodeInstance>;
  edges: Record<EdgeId, EdgeInstance>;
}

// TODO: move
export type DAGRelation = { parent: NodeId | null; children: NodeId[] };
export type DAG = Record<NodeId, DAGRelation>;

// TODO: move
export const buildNodeNumbers = (graph: Graph): Record<NodeId, string> => {
  const parentOf: Record<NodeId, NodeId | null> = {};
  const childrenOf: Record<NodeId, { childId: NodeId; order: number }[]> = {};

  for (const nodeId of Object.keys(graph.nodes)) {
    parentOf[nodeId] = null;
    childrenOf[nodeId] = [];
  }
  for (const edge of Object.values(graph.edges)) {
    parentOf[edge.from.nodeId] = edge.to.nodeId;
    childrenOf[edge.to.nodeId].push({ childId: edge.from.nodeId, order: edge.order });
  }
  for (const nodeId of Object.keys(childrenOf)) {
    childrenOf[nodeId].sort((a, b) => a.order - b.order);
  }

  const rootId = Object.keys(graph.nodes).find((id) => parentOf[id] === null);
  if (!rootId) return {};

  const result: Record<NodeId, string> = { [rootId]: '0' };
  const queue: NodeId[] = [rootId];

  while (queue.length > 0) {
    const current = queue.shift()!;
    const parentNum = result[current];
    childrenOf[current].forEach(({ childId }, index) => {
      result[childId] =
        parentNum === '0' ? String(index + 1) : `${parentNum}.${index + 1}`;
      queue.push(childId);
    });
  }

  return result;
};

// TODO: move
export const buildDag = (graph: Graph): DAG => {
  const dag: DAG = {};

  for (const nodeId of getKeys(graph.nodes)) {
    dag[nodeId] = { parent: null, children: [] };
  }

  for (const edge of getValues(graph.edges)) {
    const child = edge.from.nodeId;
    const parent = edge.to.nodeId;

    dag[child].parent = parent;
    dag[parent].children.push(child);
  }

  return dag;
};

// --- validation ---
export enum IssueSeverity {
  Error = 'error',
  Warning = 'warning',
  Info = 'info',
}

export type IssueLocation =
  | { kind: 'graph'; graphId: GraphId }
  | { kind: 'node'; graphId: GraphId; nodeId: NodeId }
  | { kind: 'port'; graphId: GraphId; nodeId: NodeId; portId: PortId };

export enum ValidationIssueCode {
  MISSING_INPUT = 'missing_input',
  TYPE_MISMATCH = 'type_mismatch',
  OTHER = 'other',
}

export enum NodeValidationStatus {
  Ok = 'ok',
  Warning = 'warning',
  Error = 'error',
}

export type ValidationIssue = {
  id: string;
  severity: IssueSeverity;
  code: ValidationIssueCode;
  message: string;
  location: IssueLocation;
};

export type NodeValidation = {
  nodeId: NodeId;
  // Status derived from validation issues present in the graph validation.
  status: NodeValidationStatus;
  // Inferred types per port.
  inferredTypes: Record<PortId, InferredPortType>;
  // Convenience tracker for issues related to the current node, present in the graph validation.
  issueIds: string[];
};

export type GraphValidation = {
  graphId: GraphId;
  // Graph version this validation corresponds to.
  graphVersion: number;
  // List of issues for this graph version.
  issues: ValidationIssue[];
  // Per node validation results.
  nodes: Record<NodeId, NodeValidation>;
  // Unix timestamp of when this graph was last validated.
  validatedAt: number;
};

// --- evaluation ---
export enum EvaluationErrorType {
  CONNECTION_ERROR = 'connection_error',
  OTHER = 'other',
}

export enum NodeEvaluationStatus {
  Ok = 'ok',
  Error = 'error',
  Idle = 'idle',
  Running = 'running',
}

export enum EvaluationValueKind {
  Empty = 'empty',
  String = 'string',
  Boolean = 'boolean',
  Number = 'number',
  Array = 'array',
  Object = 'object',
}

export type EvaluationValue =
  | { kind: EvaluationValueKind.Empty }
  | { kind: EvaluationValueKind.String; value: string }
  | { kind: EvaluationValueKind.Boolean; value: boolean }
  | { kind: EvaluationValueKind.Number; value: number }
  | { kind: EvaluationValueKind.Array; value: unknown[] }
  | { kind: EvaluationValueKind.Object; value: Record<string, unknown> };

export type EvaluationError = {
  code: EvaluationErrorType;
  message: string;
};

export type NodeEvaluation = {
  nodeId: NodeId;

  status: NodeEvaluationStatus;

  valueRef: EvaluationValue;
  error: EvaluationError | null;

  // Computed from the evaluatorVersion, configuration and values of its connected inputs.
  // Used to have fine-grained cache invalidations when editing a graph.
  fingerprint: number;
};

/**
 * Caches the evaluated template code for each node and the final graph result.
 * Its responsibility is describing the results of (a subset of) a template for a specific input context (e.g. file).
 */
export type GraphEvaluation = {
  graphId: GraphId;
  // Must match the graph version used to produce the stored results.
  // If not it means the evaluation result is stale (i.e. a command was executed over the graph).
  graphVersion: number;
  // Hash based on the input context (file). If it does not match the evaluation is stale.
  inputContextHash: string;
  // Tracks the behaviour of evaluation and validation, if any of these semantics are changed bump this version.
  // It marks the evaluation result as stale when a user loads an existing template in the editor.
  evaluatorVersion: string;
  // Unix timestamp of when this result was computed or last modified.
  evaluatedAt: number;
  // Rendered template fragments per node.
  nodeTemplates: Record<NodeId, any>;
  // Per node evaluation results.
  nodes: Record<NodeId, NodeEvaluation>;
};
